import { Controller } from '@hotwired/stimulus';
import moment from 'moment-timezone';

const STATUS = {
    queued:     { dot: 'bcast-idx__dot--queued',     label: 'Queued' },
    gathering:  { dot: 'bcast-idx__dot--gathering',  label: 'Gathering list of people' },
    running:    { dot: 'bcast-idx__dot--running',    label: 'Running' },
    finished:   { dot: 'bcast-idx__dot--finished',   label: 'Finished' },
    failed:     { dot: 'bcast-idx__dot--failed',     label: 'Failed' },
};

const FREQ_UNIT = {
    DAILY:       'day(s)',
    EVERYWEEKDAY: 'week(s)',
    WEEKLY:      'week(s)',
    MONTHLY:     'month(s)',
};

export default class extends Controller {
    static targets = [
        // dashboard stats
        'statTotal', 'statSent', 'statDeliveryRate', 'statActive',
        'barQueued', 'barGathering', 'barRunning', 'barFinished', 'barFailed',
        'countQueued', 'countGathering', 'countRunning', 'countFinished', 'countFailed',
        // table
        'heading', 'search', 'toggle', 'rows', 'emptyState',
        'selectAll', 'archiveBtn', 'restoreBtn', 'bulkActions',
        // modal / notifications
        'modal', 'createNotice', 'createNoticeMessage', 'createSuccess',
        // form: broadcast + message
        'messageSelect', 'messageWarning',
        // form: audience
        'hasAllUsersCheck', 'hasSegmentsCheck', 'segmentsPanel', 'segmentsInSelect', 'segmentsExSelect',
        'hasChannelsCheck', 'channelsPanel', 'channelsInSelect', 'channelsExSelect', 'channelsRefreshBtn',
        'hasAttributesCheck', 'attributesPanel', 'attributeFiltersList', 'addAttributeFilterBtn',
        // form: section error states
        'broadcastCard', 'audienceCard', 'scheduleCard',
        'broadcastCardError', 'audienceCardError', 'scheduleCardError',
        // form: schedule
        'dateInput', 'timeInput', 'tzDisplay',
        'recurringCheck', 'recurringSection',
        'frequencySelect', 'intervalSelect', 'intervalUnit',
        'weekdaysSection', 'monthdaySection', 'monthdaySelect',
        'endsOnSection', 'endsAfterSection', 'endDateInput', 'maxOccurrencesInput',
    ];

    static values = {
        botId:        Number,
        broadcasts:   Array,
        showBaseUrl:  String,
        duplicateBaseUrl: String,
        createUrl:    String,
        messages:     Array,
        segments:     Array,
        userAttributes: { type: Array, default: [] },
        attributeOperators: { type: Array, default: [] },
        operatorsWithoutOperands: { type: Array, default: [] },
        channelsUrl:        String,
        channelsRefreshUrl: { type: String, default: '' },
        userTimezone: String,
        tab:             { type: String, default: 'active' },
        archiveUrl:      { type: String, default: '' },
        restoreUrl:      { type: String, default: '' },
        archiveCsrfToken: { type: String, default: '' },
        restoreCsrfToken: { type: String, default: '' },
        viewActiveUrl:    { type: String, default: '' },
        viewArchivedUrl:  { type: String, default: '' },
    };

    // ─── Lifecycle ──────────────────────────────────────────────────────────────

    connect() {
        this.query        = '';
        this.statusFilter = 'all';
        this.broadcasts   = this.broadcastsValue || [];
        this.formTimezone = this.userTimezoneValue || 'UTC';
        this.sortColumn   = null;
        this.sortDirection = null;

        this.handleSearchChange = (e) => { this.query = (e.detail || '').toString().trim().toLowerCase(); this.render(); };
        this.handleSearchClear  = ()  => { this.query = ''; this.render(); };
        this.handleToggleChange = (e) => { this.statusFilter = (e.detail || 'all').toString(); this.render(); };

        this.searchTarget.addEventListener('valueChange', this.handleSearchChange);
        this.searchTarget.addEventListener('clear',       this.handleSearchClear);
        this.toggleTarget.addEventListener('selectedToggleChange', this.handleToggleChange);

        this.updateSortHeaders();
        this.render();
        this.updateBulkActionsVisibility();
    }

    ensureChannelsSearchInit() {
        // Lazy: init on first panel-open so we run after app.js's DOM-ready
        // resetUI applies its vanilla `.select2()` to every <select>. Running
        // eagerly in connect() races with that init and can end up wrapped
        // in a non-AJAX Select2 that never fires a request.
        if (this._channelsSearchInitialized) {
            return;
        }
        this._channelsSearchInitialized = true;
        this.initChannelsSearch(this.channelsInSelectTarget);
        this.initChannelsSearch(this.channelsExSelectTarget);
    }

    initChannelsSearch(select) {
        const url = this.channelsUrlValue;
        if (!url) {
            return;
        }
        const $sel = $(select);
        if ($sel.hasClass('select2-hidden-accessible')) {
            $sel.select2('destroy');
        }
        $sel.select2({
            width: '100%',
            minimumInputLength: 2,
            multiple: true,
            ajax: {
                url,
                type: 'GET',
                dataType: 'json',
                delay: 500,
                data: (params) => ({ q: params.term || '' }),
                processResults: (data) => ({
                    results: (data.results || []).map((item) => ({
                        id: item.id,
                        text: item.name || item.text || item.id,
                    })),
                }),
                cache: true,
            },
        });
    }

    preselectChannels(select, ids) {
        const $sel = $(select);
        ids.forEach((id) => {
            if (!$sel.find(`option[value="${$.escapeSelector(String(id))}"]`).length) {
                $sel.append(new Option(String(id), String(id), true, true));
            }
        });
        if (ids.length) {
            $sel.val(ids.map(String)).trigger('change');
        }
    }

    disconnect() {
        this.searchTarget.removeEventListener('valueChange', this.handleSearchChange);
        this.searchTarget.removeEventListener('clear',       this.handleSearchClear);
        this.toggleTarget.removeEventListener('selectedToggleChange', this.handleToggleChange);
        clearTimeout(this.successNoticeTimer);
    }

    // ─── Modal ──────────────────────────────────────────────────────────────────

    openCreateModal({ forcePopulate = false } = {}) {
        this.show(this.createNoticeTarget, false);
        this.clearSectionErrors();
        this.formTimezone = this.userTimezoneValue || 'UTC';
        this.populateFormSelects({ force: forcePopulate });
        this.tzDisplayTarget.textContent = this.formTimezone;
        this.updateAttributeAvailability();
        this.show(this.modalTarget, true);
        document.body.style.overflow = 'hidden';
        document.body.classList.add('bcast-drawer-open');
    }

    closeCreateModal() {
        this.show(this.createNoticeTarget, false);
        this.show(this.modalTarget, false);
        document.body.style.overflow = '';
        document.body.classList.remove('bcast-drawer-open');
        this.resetForm();
    }

    navigateToView() {
        const url = this.tabValue === 'archived' ? this.viewActiveUrlValue : this.viewArchivedUrlValue;
        if (url) {
            window.location.href = url;
        }
    }

    /** Native message <select> (explicit id — Stimulus target can be unreliable with some DOM setups). */
    getMessageSelectElement() {
        return this.element.querySelector('#bf-message')
            || (this.hasMessageSelectTarget ? this.messageSelectTarget : null);
    }

    messagesListFromValue() {
        const raw = this.messagesValue;
        if (Array.isArray(raw)) {
            return raw;
        }
        if (raw && typeof raw === 'object') {
            return Object.values(raw);
        }

        return [];
    }

    parseDuplicateMessageId(data) {
        if (!data || typeof data !== 'object') {
            return NaN;
        }
        const raw = data.messageId ?? data.message_id;
        if (typeof raw === 'number' && Number.isFinite(raw)) {
            return raw;
        }
        const n = parseInt(String(raw ?? '').trim(), 10);

        return Number.isFinite(n) ? n : NaN;
    }

    // ─── Form: populate selects ──────────────────────────────────────────────

    populateFormSelects({ force = false } = {}) {
        if (force) {
            this._selectsPopulated = false;
        }

        // Populate once (or again when force is true)
        if (!this._selectsPopulated) {
            this._selectsPopulated = true;

            // Messages
            const messages = this.messagesListFromValue();
            const msgSel   = this.getMessageSelectElement();
            if (msgSel) {
                // Clear the placeholder
                while (msgSel.options.length > 1) msgSel.remove(1);
                if (messages.length === 0) {
                    const opt = document.createElement('option');
                    opt.value    = '';
                    opt.disabled = true;
                    opt.textContent = 'No messages available — create one first';
                    msgSel.appendChild(opt);
                } else {
                    messages.forEach((m) => {
                        const opt = document.createElement('option');
                        const mid = m.id ?? m.message_id ?? m.messageId;
                        opt.value       = mid != null ? String(mid) : '';
                        const title = m.title ?? m.message_title ?? m.messageTitle;
                        opt.textContent = title != null ? String(title) : '';
                        msgSel.appendChild(opt);
                    });
                }
            }

            // Segments
            const segments = this.segmentsValue || [];
            const inSel    = this.segmentsInSelectTarget;
            const exSel    = this.segmentsExSelectTarget;

            // Clear loading placeholder
            inSel.innerHTML = '';
            exSel.innerHTML = '<option value="">None</option>';

            if (segments.length === 0) {
                const opt = document.createElement('option');
                opt.value    = '';
                opt.disabled = true;
                opt.textContent = 'No segments configured for this flow';
                inSel.appendChild(opt);
            } else {
                const emptyInclude = document.createElement('option');
                emptyInclude.value = '';
                emptyInclude.textContent = '— choose —';
                inSel.appendChild(emptyInclude);

                segments.forEach((s) => {
                    const makeOpt = () => {
                        const opt = document.createElement('option');
                        opt.value       = s.id != null ? String(s.id) : '';
                        opt.textContent = s.name != null ? String(s.name) : '';
                        return opt;
                    };
                    inSel.appendChild(makeOpt());
                    exSel.appendChild(makeOpt());
                });
            }
        }

        // Always update timezone display
        this.tzDisplayTarget.textContent = this.formTimezone || this.userTimezoneValue || 'UTC';
    }

    // ─── Form: message change ────────────────────────────────────────────────

    onMessageChange() {
        this.show(this.messageWarningTarget, false);
        this.clearSectionError('broadcast');
    }

    // ─── Form: audience toggles ──────────────────────────────────────────────

    onAudienceTargetChange() {
        this.updateAttributeAvailability();
        this.clearSectionError('audience');
    }

    onHasSegmentsChange() {
        this.show(this.segmentsPanelTarget, this.hasSegmentsCheckTarget.checked);
        this.onAudienceTargetChange();
    }

    onHasChannelsChange() {
        const show = this.hasChannelsCheckTarget.checked;
        this.show(this.channelsPanelTarget, show);
        this.onAudienceTargetChange();
        if (show) {
            this.ensureChannelsSearchInit();
        }
    }

    onHasAttributesChange() {
        const checked = this.hasAttributesCheckTarget.checked;
        if (checked && !this.hasTargetAudienceSelected()) {
            this.hasAttributesCheckTarget.checked = false;
            this.show(this.attributesPanelTarget, false);
            this.showSectionError('audience', 'Select Flow audience, Segments, or Channels before adding an attribute filter.');
            return;
        }

        this.show(this.attributesPanelTarget, checked);
        if (checked && this.attributeFiltersListTarget.children.length === 0) {
            this.addAttributeFilter();
        }
        this.clearSectionError('audience');
    }

    hasTargetAudienceSelected() {
        return this.hasAllUsersCheckTarget.checked ||
            this.hasSegmentsCheckTarget.checked ||
            this.hasChannelsCheckTarget.checked;
    }

    updateAttributeAvailability() {
        const hasTargetAudience = this.hasTargetAudienceSelected();

        this.hasAttributesCheckTarget.closest('.bcast-form__check-row')?.classList.toggle('bcast-form__check-row--disabled', !hasTargetAudience);

        if (!hasTargetAudience) {
            this.hasAttributesCheckTarget.checked = false;
        }

        this.show(this.attributesPanelTarget, this.hasAttributesCheckTarget.checked);
    }

    addAttributeFilter(event = null) {
        if (event) {
            event.preventDefault();
        }

        const row = this.buildAttributeFilterRow();
        this.attributeFiltersListTarget.appendChild(row);
    }

    removeAttributeFilter(event) {
        event.preventDefault();
        const row = event.currentTarget.closest('.bcast-form__attribute-filter');
        if (row) {
            row.remove();
            this.refreshAttributeFilterLogicLabels();
        }
        if (this.attributeFiltersListTarget.children.length === 0) {
            this.hasAttributesCheckTarget.checked = false;
            this.show(this.attributesPanelTarget, false);
        }
        this.clearSectionError('audience');
    }

    onAttributeOperatorChange(event) {
        this.updateAttributeOperandVisibility(event.currentTarget.closest('.bcast-form__attribute-filter'));
        this.clearSectionError('audience');
    }

    buildAttributeFilterRow(filter = {}) {
        const row = document.createElement('div');
        row.className = 'bcast-form__attribute-filter';

        const logic = document.createElement('div');
        logic.className = 'bcast-form__attribute-logic';
        logic.textContent = this.attributeFiltersListTarget.children.length > 0 ? 'AND' : '';
        row.appendChild(logic);

        const fields = document.createElement('div');
        fields.className = 'bcast-form__attribute-fields';
        fields.style.display = 'grid';
        fields.style.gridTemplateColumns = 'minmax(0, 1.25fr) minmax(130px, 0.75fr) minmax(0, 1fr) auto';
        fields.style.gap = '10px';
        fields.style.alignItems = 'start';

        fields.appendChild(this.buildAttributeSelect(filter.field || ''));
        fields.appendChild(this.buildOperatorSelect(filter.operator || 'eq'));
        fields.appendChild(this.buildOperandInput(filter.operand || ''));

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'bcast-form__now-btn bcast-form__remove-filter-btn';
        removeBtn.style.alignSelf = 'end';
        removeBtn.style.minHeight = '37px';
        removeBtn.textContent = 'Remove';
        removeBtn.setAttribute('data-action', 'click->broadcasting-v2-index#removeAttributeFilter');
        fields.appendChild(removeBtn);

        row.appendChild(fields);
        this.updateAttributeOperandVisibility(row);

        return row;
    }

    buildAttributeSelect(selectedValue) {
        const field = this.buildFilterField('Attribute');
        const select = document.createElement('select');
        select.className = 'bcast-form__select';
        select.dataset.filterField = 'field';
        select.setAttribute('aria-label', 'Select a user attribute');
        select.setAttribute('data-action', 'change->broadcasting-v2-index#clearAudienceError');

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select a user attribute';
        select.appendChild(placeholder);

        (this.userAttributesValue || []).forEach((attribute) => {
            const opt = document.createElement('option');
            opt.value = attribute.id != null ? String(attribute.id) : '';
            opt.textContent = attribute.displayName != null ? String(attribute.displayName) : '';
            if (opt.value === String(selectedValue)) {
                opt.selected = true;
            }
            select.appendChild(opt);
        });

        field.appendChild(select);

        return field;
    }

    buildOperatorSelect(selectedValue) {
        const field = this.buildFilterField('Condition');
        const select = document.createElement('select');
        select.className = 'bcast-form__select';
        select.dataset.filterOperator = 'operator';
        select.setAttribute('aria-label', 'Select a user attribute comparison');
        select.setAttribute('data-action', 'change->broadcasting-v2-index#onAttributeOperatorChange');

        (this.attributeOperatorsValue || []).forEach((operator) => {
            const opt = document.createElement('option');
            opt.value = operator.value != null ? String(operator.value) : '';
            opt.textContent = operator.label != null ? String(operator.label) : '';
            if (opt.value === String(selectedValue)) {
                opt.selected = true;
            }
            select.appendChild(opt);
        });

        field.appendChild(select);

        return field;
    }

    buildOperandInput(value) {
        const field = this.buildFilterField('Value');
        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 191;
        input.className = 'bcast-form__input';
        input.dataset.filterOperand = 'operand';
        input.placeholder = 'Attribute value';
        input.value = value != null ? String(value) : '';
        input.setAttribute('aria-label', 'User attribute value');
        input.setAttribute('data-action', 'input->broadcasting-v2-index#clearAudienceError');

        field.appendChild(input);

        return field;
    }

    buildFilterField(labelText) {
        const field = document.createElement('div');
        field.className = 'bcast-form__field';

        const label = document.createElement('label');
        label.className = 'bcast-form__label';
        label.textContent = labelText;
        field.appendChild(label);

        return field;
    }

    updateAttributeOperandVisibility(row) {
        if (!row) {
            return;
        }

        const operator = row.querySelector('[data-filter-operator]')?.value || '';
        const operand = row.querySelector('[data-filter-operand]');
        const withoutOperand = (this.operatorsWithoutOperandsValue || []).map(String).includes(operator);
        if (operand) {
            operand.disabled = withoutOperand;
            const operandField = operand.closest('.bcast-form__field');
            if (operandField) {
                operandField.style.display = withoutOperand ? 'none' : '';
            } else {
                operand.style.display = withoutOperand ? 'none' : '';
            }
            if (withoutOperand) {
                operand.value = '';
            }
        }
    }

    refreshAttributeFilterLogicLabels() {
        [...this.attributeFiltersListTarget.children].forEach((row, index) => {
            const logic = row.querySelector('.bcast-form__attribute-logic');
            if (logic) {
                logic.textContent = index > 0 ? 'AND' : '';
            }
        });
    }

    collectAttributeFilters() {
        return [...this.attributeFiltersListTarget.querySelectorAll('.bcast-form__attribute-filter')].map((row) => ({
            field: row.querySelector('[data-filter-field]')?.value || '',
            operator: row.querySelector('[data-filter-operator]')?.value || '',
            operand: row.querySelector('[data-filter-operand]')?.value.trim() || '',
        }));
    }

    validateAttributeFilters(targetFilters) {
        if (targetFilters.length === 0) {
            return 'Please add an attribute filter or unselect Attributes.';
        }

        for (const targetFilter of targetFilters) {
            if (!targetFilter.field) {
                return targetFilters.length === 1
                    ? 'Please ensure you select a user attribute or unselect Attributes as a filter.'
                    : 'Please ensure you select a user attribute or remove the attribute filter.';
            }
            if (!targetFilter.operator) {
                return 'Please ensure you have selected a user attribute comparison.';
            }
            const withoutOperand = (this.operatorsWithoutOperandsValue || []).map(String).includes(targetFilter.operator);
            if (!withoutOperand && !targetFilter.operand) {
                return 'Please type a user attribute value for your filter.';
            }
        }

        return null;
    }

    clearSectionError(section) {
        const card = this[`${section}CardTarget`];
        const errorEl = this[`${section}CardErrorTarget`];
        if (card) card.classList.remove('bcast-form__card--error');
        if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
        }
    }

    clearBroadcastError() { this.clearSectionError('broadcast'); }
    clearAudienceError() { this.clearSectionError('audience'); }
    clearScheduleError() { this.clearSectionError('schedule'); }

    async refreshChannels() {
        const url = this.channelsRefreshUrlValue;
        if (!url) {
            return;
        }

        this.clearSectionError('audience');

        const btn = this.hasChannelsRefreshBtnTarget ? this.channelsRefreshBtnTarget : null;
        if (btn) {
            btn.disabled = true;
        }

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
            });
            const data = await res.json();
            const results = data.results || [];
            this.populateChannelSelectsFromResults(results, { preserveSelection: true });
        } catch {
            this.showSectionError('audience', 'Could not refresh channels. Try again.');
        } finally {
            if (btn) {
                btn.disabled = false;
            }
        }
    }

    populateChannelSelectsFromResults(results, { preserveSelection = false } = {}) {
        const includedSel = preserveSelection
            ? [...this.channelsInSelectTarget.selectedOptions].map((o) => o.value).filter(Boolean)
            : [];
        const excludedSel = preserveSelection
            ? [...this.channelsExSelectTarget.selectedOptions].map((o) => o.value).filter(Boolean)
            : [];

        const chInSel = this.channelsInSelectTarget;
        const chExSel = this.channelsExSelectTarget;

        chInSel.innerHTML = '';
        chExSel.innerHTML = '';

        if (results.length === 0) {
            chInSel.innerHTML = '<option value="" disabled>No channels configured for this flow</option>';
            chExSel.innerHTML = '<option value="">None</option>';
            return;
        }

        results.forEach((ch) => {
            const optIn = document.createElement('option');
            optIn.value = ch.id;
            optIn.textContent = ch.name;
            chInSel.appendChild(optIn);

            const optEx = document.createElement('option');
            optEx.value = ch.id;
            optEx.textContent = ch.name;
            chExSel.appendChild(optEx);
        });

        if (preserveSelection) {
            this.setSelectValues(chInSel, includedSel);
            this.setSelectValues(chExSel, excludedSel);
        }
    }

    // ─── Form: schedule / recurring ─────────────────────────────────────────

    setToNow() {
        const tz    = this.formTimezone || this.userTimezoneValue || 'UTC';
        const now   = new Date();
        const opts  = { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', hour12: false };
        const parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(now);
        const get   = (t) => parts.find((p) => p.type === t)?.value ?? '';

        this.dateInputTarget.value = `${get('year')}-${get('month')}-${get('day')}`;
        this.timeInputTarget.value = `${get('hour')}:${get('minute')}`;
    }

    onRecurringChange() {
        const on = this.recurringCheckTarget.checked;
        this.show(this.recurringSectionTarget, on);
        if (on) this.onFrequencyChange();
    }

    onFrequencyChange() {
        const freq = this.frequencySelectTarget.value;
        this.intervalUnitTarget.textContent = FREQ_UNIT[freq] || 'time(s)';
        const showWeekdays = freq === 'WEEKLY';
        const showMonthday = freq === 'MONTHLY';
        if (showWeekdays) {
            this.weekdaysSectionTarget.style.removeProperty('display');
        } else {
            this.weekdaysSectionTarget.style.display = 'none';
        }
        this.show(this.monthdaySectionTarget, showMonthday);
    }

    onEndsChange(event) {
        const val = event.target.value;
        this.show(this.endsOnSectionTarget,    val === 'on');
        this.show(this.endsAfterSectionTarget, val === 'after');
    }

    // ─── Form: create ────────────────────────────────────────────────────────

    isScheduledInPast(dateStr, timeStr, timeZone) {
        const m = moment.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm', timeZone);
        if (!m.isValid()) {
            return false;
        }
        const startMinuteUtc = m.clone().utc().startOf('minute');
        const nowMinuteUtc = moment().utc().startOf('minute');
        return startMinuteUtc.isBefore(nowMinuteUtc);
    }

    async createBroadcast() {
        const msgSelCreate = this.getMessageSelectElement();
        const messageId = msgSelCreate ? msgSelCreate.value : '';
        const date      = this.dateInputTarget.value;
        const time      = this.timeInputTarget.value;

        const audienceSelected =
            this.hasAllUsersCheckTarget.checked ||
            this.hasSegmentsCheckTarget.checked ||
            this.hasChannelsCheckTarget.checked;

        this.clearSectionErrors();

        if (!messageId || !date || !time || !audienceSelected) {
            const hasErrors = [];
            if (!messageId) {
                this.showSectionError('broadcast', 'Please select a message.');
                hasErrors.push('broadcast');
            }
            if (!date || !time) {
                this.showSectionError('schedule', !date && !time ? 'Please enter date and time.' : (!date ? 'Please enter a date.' : 'Please enter a time.'));
                hasErrors.push('schedule');
            }
            if (!audienceSelected) {
                this.showSectionError('audience', 'Select at least one audience (Flow audience, Segments, or Channels).');
                hasErrors.push('audience');
            }
            this.showCreateError(`Please fix the ${hasErrors.length} error${hasErrors.length > 1 ? 's' : ''} below.`);
            return;
        }

        const hasChannels = this.hasChannelsCheckTarget.checked;
        const includedChannels = hasChannels ? [...this.channelsInSelectTarget.selectedOptions].map((o) => o.value).filter(Boolean) : [];
        const excludedChannels = hasChannels ? [...this.channelsExSelectTarget.selectedOptions].map((o) => o.value).filter(Boolean) : [];

        if (hasChannels && includedChannels.length === 0 && excludedChannels.length === 0) {
            this.showSectionError('audience', 'When using Channels, select at least one channel to include or exclude.');
            this.showCreateError('Please fix the error below.');
            return;
        }

        const buckets = [...this.segmentsInSelectTarget.selectedOptions].map((o) => o.value).filter(Boolean);
        const excludedBuckets = [...this.segmentsExSelectTarget.selectedOptions].map((o) => o.value).filter(Boolean);
        if (this.hasSegmentsCheckTarget.checked && buckets.length === 0 && excludedBuckets.length === 0) {
            this.showSectionError('audience', 'When using Segments, select at least one segment to include or exclude.');
            this.showCreateError('Please fix the error below.');
            return;
        }

        const hasUserAttributes = this.hasAttributesCheckTarget.checked;
        const targetFilters = hasUserAttributes ? this.collectAttributeFilters() : [];
        if (hasUserAttributes) {
            const filterError = this.validateAttributeFilters(targetFilters);
            if (filterError) {
                this.showSectionError('audience', filterError);
                this.showCreateError('Please fix the error below.');
                return;
            }
        }

        const isRecurring = this.recurringCheckTarget.checked;
        if (isRecurring) {
            const freq = this.frequencySelectTarget.value;
            if (freq === 'WEEKLY') {
                const weekDays = [...this.element.querySelectorAll('.bcast-form__day-pill input[type="checkbox"]:checked')]
                    .map((cb) => cb.value).filter(Boolean);
                if (weekDays.length === 0) {
                    this.showSectionError('schedule', 'Please select at least one day for weekly recurrence.');
                    this.showCreateError('Please fix the error below.');
                    return;
                }
            }
            const endsRadio = this.element.querySelector('input[name="bcast-ends"]:checked');
            const ends = endsRadio ? endsRadio.value : 'never';
            if (ends === 'on' && this.hasEndDateInputTarget && !this.endDateInputTarget.value) {
                this.showSectionError('schedule', 'Please enter an end date.');
                this.showCreateError('Please fix the error below.');
                return;
            }
            if (ends === 'after' && this.hasMaxOccurrencesInputTarget) {
                const n = parseInt(this.maxOccurrencesInputTarget.value, 10);
                if (!n || n < 1) {
                    this.showSectionError('schedule', 'Please enter the number of occurrences.');
                    this.showCreateError('Please fix the error below.');
                    return;
                }
            }
        }

        const scheduleTz = this.formTimezone || this.userTimezoneValue || 'UTC';
        if (!isRecurring && this.isScheduledInPast(date, time, scheduleTz)) {
            this.showSectionError('schedule', 'Scheduled date cannot be in the past.');
            this.showCreateError('Please fix the error below.');
            return;
        }

        const payload = {
            messageId: parseInt(messageId, 10),
            date,
            time,
            timezone: scheduleTz,
            hasAllUsers: this.hasAllUsersCheckTarget.checked,
            hasSegments: this.hasSegmentsCheckTarget.checked,
            hasChannels,
            hasUserAttributes,
            buckets,
            excludedBuckets,
            includedChannels,
            excludedChannels,
            targetFilters,
        };

        if (isRecurring) {
            payload.isRecurring = true;
            payload.frequency = this.frequencySelectTarget.value;
            payload.interval = parseInt(this.intervalSelectTarget.value, 10) || 1;
            if (payload.frequency === 'WEEKLY') {
                payload.weekDays = [...this.element.querySelectorAll('.bcast-form__day-pill input[type="checkbox"]:checked')]
                    .map((cb) => cb.value).filter(Boolean);
            }
            if (payload.frequency === 'MONTHLY') {
                payload.monthDay = parseInt(this.monthdaySelectTarget.value, 10) || 1;
            }
            const endsRadio = this.element.querySelector('input[name="bcast-ends"]:checked');
            payload.ends = endsRadio ? endsRadio.value : 'never';
            if (payload.ends === 'on' && this.hasEndDateInputTarget) {
                payload.endDate = this.endDateInputTarget.value || '';
            }
            if (payload.ends === 'after' && this.hasMaxOccurrencesInputTarget) {
                payload.maxOccurrences = parseInt(this.maxOccurrencesInputTarget.value, 10) || 0;
            }
        }

        try {
            const res = await fetch(this.createUrlValue, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (res.ok && data.showUrl) {
                this.closeCreateModal();
                window.location.href = data.showUrl;
            } else {
                const msg = this.extractErrorMessage(data) || 'Something went wrong. Please try again.';
                this.showCreateError(msg);
            }
        } catch {
            this.showCreateError('Unable to create broadcast. Please check your connection and try again.');
        }
    }

    showCreateError(message) {
        if (this.hasCreateNoticeMessageTarget) {
            this.createNoticeMessageTarget.setAttribute('heading', message);
            if (typeof this.createNoticeMessageTarget.heading !== 'undefined') {
                this.createNoticeMessageTarget.heading = message;
            }
        }
        this.show(this.createNoticeTarget, true);
    }

    showSectionError(section, message) {
        const card = this[`${section}CardTarget`];
        const errorEl = this[`${section}CardErrorTarget`];
        if (card && errorEl) {
            card.classList.add('bcast-form__card--error');
            errorEl.textContent = message;
            errorEl.style.display = '';
        }
    }

    clearSectionErrors() {
        for (const section of ['broadcast', 'audience', 'schedule']) {
            const card = this[`${section}CardTarget`];
            const errorEl = this[`${section}CardErrorTarget`];
            if (card) card.classList.remove('bcast-form__card--error');
            if (errorEl) {
                errorEl.textContent = '';
                errorEl.style.display = 'none';
            }
        }
    }

    extractErrorMessage(data) {
        if (!data || typeof data !== 'object') return null;
        if (typeof data.message === 'string') return data.message;
        if (typeof data.error === 'string') return data.error;
        if (data.error && typeof data.error.message === 'string') return data.error.message;
        if (typeof data.detail === 'string') return data.detail;
        return null;
    }

    flashSuccess() {
        if (!this.hasCreateSuccessTarget) return;
        this.createSuccessTarget.hidden = false;
        clearTimeout(this.successNoticeTimer);
        this.successNoticeTimer = setTimeout(() => { this.createSuccessTarget.hidden = true; }, 4000);
    }

    resetForm() {
        this.formTimezone = this.userTimezoneValue || 'UTC';
        // Text/number/date/time inputs
        [this.dateInputTarget, this.timeInputTarget,
         this.endDateInputTarget, this.maxOccurrencesInputTarget].forEach((el) => {
            if (el) el.value = '';
        });
        // Selects
        const msgSelReset = this.getMessageSelectElement();
        if (msgSelReset) msgSelReset.value = '';
        this.segmentsInSelectTarget.value = '';
        this.segmentsExSelectTarget.value = '';
        this.channelsInSelectTarget.value = '';
        this.channelsExSelectTarget.value = '';
        // Checkboxes
        [this.hasAllUsersCheckTarget, this.hasSegmentsCheckTarget,
         this.hasChannelsCheckTarget, this.hasAttributesCheckTarget, this.recurringCheckTarget].forEach((el) => { el.checked = false; });
        // Collapse all sub-panels
        this.show(this.segmentsPanelTarget,    false);
        this.show(this.channelsPanelTarget,    false);
        this.show(this.attributesPanelTarget,  false);
        this.show(this.recurringSectionTarget, false);
        this.show(this.endsOnSectionTarget,    false);
        this.show(this.endsAfterSectionTarget, false);
        this.show(this.messageWarningTarget,   false);
        this.show(this.createNoticeTarget,     false);
        this.resetAttributeFilters();
        this.updateAttributeAvailability();
        // Reset "Never" radio
        this.element.querySelector('input[name="bcast-ends"][value="never"]').checked = true;
        // Reset frequency to Every weekday and interval unit
        if (this.hasFrequencySelectTarget) {
            this.frequencySelectTarget.value = 'EVERYWEEKDAY';
            this.onFrequencyChange();
        }
        this.tzDisplayTarget.textContent = this.formTimezone;
        // Clear weekday checkboxes
        this.element.querySelectorAll('.bcast-form__day-pill input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
    }

    resetAttributeFilters() {
        const rows = [...this.attributeFiltersListTarget.querySelectorAll('.bcast-form__attribute-filter')];
        if (rows.length === 0) {
            this.addAttributeFilter();
            return;
        }

        rows.forEach((row, index) => {
            if (index > 0) {
                row.remove();
                return;
            }

            row.querySelector('[data-filter-field]').value = '';
            row.querySelector('[data-filter-operator]').value = 'eq';
            const operand = row.querySelector('[data-filter-operand]');
            if (operand) {
                operand.value = '';
                operand.disabled = false;
                operand.style.display = '';
                operand.closest('.bcast-form__field')?.style.removeProperty('display');
            }
        });
        this.refreshAttributeFilterLogicLabels();
    }

    async duplicateBroadcast(event) {
        const id = Number(event?.currentTarget?.dataset?.broadcastId || 0);
        if (!Number.isFinite(id) || id <= 0) return;

        this.openCreateModal({ forcePopulate: true });
        this.showCreateError('Loading broadcast details...');

        try {
            const baseUrl = this.duplicateBaseUrlValue || '';
            if (!baseUrl) {
                throw new Error('Duplicate URL is not configured.');
            }

            const res = await fetch(`${baseUrl}/${id}/duplicate`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });

            const contentType = (res.headers.get('content-type') || '').toLowerCase();
            if (!contentType.includes('json')) {
                throw new Error('Unexpected response from server. Please refresh and try again.');
            }

            let data = {};
            try {
                data = await res.json();
            } catch {
                data = {};
            }

            if (!res.ok) {
                throw new Error(this.extractErrorMessage(data) || 'Unable to load duplicate data.');
            }

            await this.applyDuplicatePayload(data);
            this.show(this.createNoticeTarget, false);
        } catch (error) {
            this.showCreateError(error?.message || 'Unable to duplicate this broadcast right now.');
        }
    }

    async applyDuplicatePayload(payload) {
        const data = payload && typeof payload === 'object' ? payload : {};

        this.formTimezone = (data.timezone || this.userTimezoneValue || 'UTC').toString();
        this.tzDisplayTarget.textContent = this.formTimezone;

        const messageIdNum = this.parseDuplicateMessageId(data);
        const messageIdOk = Number.isFinite(messageIdNum) && messageIdNum > 0;
        const titleFromPayload = data.messageTitle ?? data.message_title;
        this.ensureMessageOptionSelected(
            messageIdOk ? messageIdNum : null,
            titleFromPayload != null ? String(titleFromPayload) : '',
        );

        this.hasAllUsersCheckTarget.checked = Boolean(data.hasAllUsers);
        this.hasSegmentsCheckTarget.checked = Boolean(data.hasSegments);
        this.onHasSegmentsChange();
        this.setSingleSelectFirstMatch(this.segmentsInSelectTarget, data.buckets || []);
        this.setSingleSelectFirstMatch(this.segmentsExSelectTarget, data.excludedBuckets || []);

        this.hasChannelsCheckTarget.checked = Boolean(data.hasChannels);
        this.show(this.channelsPanelTarget, this.hasChannelsCheckTarget.checked);
        if (this.hasChannelsCheckTarget.checked) {
            this.ensureChannelsSearchInit();
            this.preselectChannels(this.channelsInSelectTarget, data.includedChannels || []);
            this.preselectChannels(this.channelsExSelectTarget, data.excludedChannels || []);
        }

        this.updateAttributeAvailability();
        this.attributeFiltersListTarget.innerHTML = '';
        this.hasAttributesCheckTarget.checked = Boolean(data.hasUserAttributes);
        this.show(this.attributesPanelTarget, this.hasAttributesCheckTarget.checked);
        if (this.hasAttributesCheckTarget.checked) {
            const filters = Array.isArray(data.targetFilters) ? data.targetFilters : [];
            if (filters.length === 0) {
                this.addAttributeFilter();
            } else {
                filters.forEach((filter) => {
                    this.attributeFiltersListTarget.appendChild(this.buildAttributeFilterRow(filter));
                });
            }
        }

        this.dateInputTarget.value = (data.date || '').toString();
        this.timeInputTarget.value = (data.time || '').toString();

        const isRecurring = Boolean(data.isRecurring);
        this.recurringCheckTarget.checked = isRecurring;
        this.onRecurringChange();

        if (isRecurring) {
            const frequency = (data.frequency || 'EVERYWEEKDAY').toString();
            this.frequencySelectTarget.value = frequency;
            this.intervalSelectTarget.value = String(Math.max(1, Number(data.interval || 1)));
            this.onFrequencyChange();

            const weekDays = Array.isArray(data.weekDays) ? data.weekDays.map((d) => d.toString()) : [];
            this.element.querySelectorAll('.bcast-form__day-pill input[type="checkbox"]').forEach((cb) => {
                cb.checked = weekDays.includes(cb.value);
            });

            if (this.hasMonthdaySelectTarget) {
                const monthDay = Number(data.monthDay || 1);
                this.monthdaySelectTarget.value = String(Math.min(31, Math.max(1, monthDay)));
            }

            const ends = (data.ends || 'never').toString();
            const endsRadio = this.element.querySelector(`input[name="bcast-ends"][value="${ends}"]`)
                || this.element.querySelector('input[name="bcast-ends"][value="never"]');
            if (endsRadio) {
                endsRadio.checked = true;
                this.onEndsChange({ target: endsRadio });
            }

            if (this.hasEndDateInputTarget) {
                this.endDateInputTarget.value = (data.endDate || '').toString();
            }

            if (this.hasMaxOccurrencesInputTarget) {
                const maxOccurrences = Number(data.maxOccurrences || 0);
                this.maxOccurrencesInputTarget.value = maxOccurrences > 0 ? String(maxOccurrences) : '';
            }
        }
    }

    setSelectValues(selectEl, rawValues) {
        if (!selectEl || !Array.isArray(rawValues)) return;

        const wanted = new Set(rawValues.map((v) => String(v)).filter(Boolean));
        [...selectEl.options].forEach((option) => {
            option.selected = wanted.has(option.value);
        });
    }

    /**
     * Segment include/exclude fields are single <select>; pick the first matching value.
     */
    setSingleSelectFirstMatch(selectEl, rawValues) {
        if (!selectEl || !Array.isArray(rawValues)) return;
        const first = rawValues.map((v) => String(v)).find((v) => v !== '');
        if (!first) {
            selectEl.value = '';
            return;
        }
        const match = [...selectEl.options].some((o) => o.value === first);
        selectEl.value = match ? first : '';
    }

    ensureMessageOptionSelected(messageId, messageTitle) {
        const apply = () => {
            const sel = this.getMessageSelectElement();
            if (!sel) return;

            if (messageId == null || !Number.isFinite(Number(messageId)) || Number(messageId) <= 0) {
                sel.value = '';
                return;
            }

            const idStr = String(messageId);
            let exists = [...sel.options].some((o) => o.value === idStr);
            if (!exists) {
                const opt = document.createElement('option');
                opt.value = idStr;
                const label = (messageTitle || '').trim() || `Message ${idStr}`;
                opt.textContent = label;
                sel.appendChild(opt);
                exists = true;
            }
            sel.value = idStr;
            const idx = [...sel.options].findIndex((o) => o.value === idStr);
            if (idx >= 0) {
                sel.selectedIndex = idx;
            }
            sel.dispatchEvent(new Event('input', { bubbles: true }));
            sel.dispatchEvent(new Event('change', { bubbles: true }));
        };

        apply();
        requestAnimationFrame(() => apply());
    }

    // ─── Dashboard render ────────────────────────────────────────────────────

    render() {
        this.renderStats();
        this.renderTable();
    }

    renderStats() {
        const all    = this.broadcasts;
        const counts = { queued: 0, gathering: 0, running: 0, finished: 0, failed: 0 };
        let totalSent = 0, totalDelivered = 0, totalFailures = 0;

        for (const b of all) {
            const s = b.status || 'finished';
            if (s in counts) counts[s]++;
            totalSent     += Number(b.sent     || 0);
            totalDelivered += Number(b.delivered ?? b.sent ?? 0);
            totalFailures += Number(b.failures  ?? 0);
        }

        const total  = all.length;
        const active = counts.running + counts.gathering;
        const ratePct = totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0;
        const rateLabel = totalSent > 0 ? `${this.formatDeliveryRate(ratePct, totalFailures)}%` : '—';

        this.statTotalTarget.textContent        = total.toLocaleString();
        this.statSentTarget.textContent         = totalSent.toLocaleString();
        this.statDeliveryRateTarget.textContent = rateLabel;
        this.statActiveTarget.textContent       = active.toLocaleString();

        const pct = (n) => (total > 0 ? `${Math.round((n / total) * 100)}%` : '0%');
        this.barQueuedTarget.style.width     = pct(counts.queued);
        this.barGatheringTarget.style.width   = pct(counts.gathering);
        this.barRunningTarget.style.width    = pct(counts.running);
        this.barFinishedTarget.style.width   = pct(counts.finished);
        this.barFailedTarget.style.width     = pct(counts.failed);

        this.countQueuedTarget.textContent    = counts.queued;
        this.countGatheringTarget.textContent = counts.gathering;
        this.countRunningTarget.textContent   = counts.running;
        this.countFinishedTarget.textContent = counts.finished;
        this.countFailedTarget.textContent   = counts.failed;
    }

    sortBy(event) {
        const th = event.currentTarget;
        const key = th?.dataset?.sortKey;
        if (!key) return;
        const defaultDir = key === 'title' ? 'asc' : 'desc';
        if (this.sortColumn !== key) {
            this.sortColumn = key;
            this.sortDirection = defaultDir;
        } else if (this.sortDirection === defaultDir) {
            this.sortDirection = defaultDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = null;
            this.sortDirection = null;
        }
        this.updateSortHeaders();
        this.render();
    }

    updateSortHeaders() {
        this.element.querySelectorAll('.bcast-idx__th--sortable').forEach((th) => {
            const key = th.dataset.sortKey;
            if (this.sortColumn && key === this.sortColumn) {
                th.dataset.sortDir = this.sortDirection;
            } else {
                delete th.dataset.sortDir;
            }
        });
    }

    renderTable() {
        const filtered = this.broadcasts.filter((b) => {
            const title      = (b.title || '').toString().toLowerCase();
            const matchSt    = this.statusFilter === 'all' || b.status === this.statusFilter;
            const matchQuery = !this.query || title.includes(this.query);
            return matchSt && matchQuery;
        });

        const sorted = this.sortColumn ? this.sortBroadcasts(filtered) : filtered;

        const total = this.broadcasts.length;
        this.headingTarget.textContent =
            filtered.length === total
                ? `${total} broadcast${total === 1 ? '' : 's'}`
                : `${filtered.length} of ${total}`;

        this.emptyStateTarget.hidden = sorted.length > 0;
        this.rowsTarget.innerHTML    = sorted.map((b) => this.renderRow(b)).join('');
        this.rowsTarget.querySelectorAll('input[data-bcast-checkbox]').forEach((cb) => {
            cb.addEventListener('change', () => this.updateBulkActionsVisibility());
        });
        this.rowsTarget.querySelectorAll('button[data-bcast-duplicate]').forEach((btn) => {
            btn.addEventListener('click', (event) => this.duplicateBroadcast(event));
        });
        this.updateBulkActionsVisibility();
    }

    sortBroadcasts(list) {
        if (!this.sortColumn || !this.sortDirection) return list;
        const key = this.sortColumn;
        const dir = this.sortDirection === 'asc' ? 1 : -1;

        const statusOrder = { queued: 0, gathering: 1, running: 2, finished: 3, failed: 4 };

        return [...list].sort((a, b) => {
            let va, vb;
            switch (key) {
            case 'status':
                va = statusOrder[a.status] ?? 5;
                vb = statusOrder[b.status] ?? 5;
                return dir * (va - vb);
            case 'title':
                va = (a.title || '').toString().toLowerCase();
                vb = (b.title || '').toString().toLowerCase();
                return dir * va.localeCompare(vb);
            case 'scheduledAt':
                va = a.scheduledAtSort || '';
                vb = b.scheduledAtSort || '';
                return dir * (va > vb ? 1 : va < vb ? -1 : 0);
            case 'audience':
            case 'sent':
            case 'failures':
                va = Number(a[key]) || 0;
                vb = Number(b[key]) || 0;
                return dir * (va - vb);
            case 'delivered':
                va = (a.sent || 0) > 0 ? ((a.delivered ?? a.sent ?? 0) / a.sent) * 100 : 0;
                vb = (b.sent || 0) > 0 ? ((b.delivered ?? b.sent ?? 0) / b.sent) * 100 : 0;
                return dir * (va - vb);
            default:
                return 0;
            }
        });
    }

    renderRow(broadcast) {
        const tab         = this.tabValue || 'active';
        const statusKey   = (broadcast.status || 'finished').toString();
        const statusMeta  = STATUS[statusKey] || STATUS.queued;
        const canArchive  = tab === 'active' && (statusKey === 'finished' || statusKey === 'failed');
        const canRestore  = tab === 'archived';
        const showCheckbox = canArchive || canRestore;

        const title      = this.esc((broadcast.title || '').toString());
        const scheduled  = this.esc((broadcast.scheduledAt || '').toString());
        const audience   = Number(broadcast.audience || 0);
        const sent       = Number(broadcast.sent     || 0);
        const delivered  = Number(broadcast.delivered ?? broadcast.sent ?? 0);
        const failures   = Number(broadcast.failures  ?? 0);
        const deliveryRate = sent > 0 ? (delivered / sent) * 100 : 0;
        const rateLabel  = sent > 0 ? `${this.formatDeliveryRate(deliveryRate, failures)}%` : '—';
        const rateClass  = sent > 0
            ? (deliveryRate >= 99.5 ? 'bcast-idx__rate--good' : deliveryRate < 50 ? 'bcast-idx__rate--low' : '')
            : '';
        const isDraft    = `${broadcast.id || ''}`.startsWith('draft-');
        const href       = `${this.showBaseUrlValue}/${broadcast.id}`;
        const titleCell  = isDraft
            ? `<span class="bcast-idx__row-title">${title}</span>`
            : `<a class="bcast-idx__row-link" href="${href}">${title}</a>`;

        const checkboxCell = showCheckbox
            ? `<td class="bcast-idx__col--checkbox"><input type="checkbox" data-bcast-checkbox data-broadcast-id="${this.esc(String(broadcast.id))}" aria-label="Select broadcast"></td>`
            : '<td class="bcast-idx__col--checkbox"></td>';
        const canDuplicate = Boolean(broadcast.canDuplicate);
        const duplicateAction = canDuplicate
            ? `<button type="button" class="bcast-idx__action-btn" data-bcast-duplicate data-broadcast-id="${this.esc(String(broadcast.id))}">Duplicate</button>`
            : '';

        return `
            <tr>
                ${checkboxCell}
                <td><span class="bcast-idx__status"><span class="bcast-idx__dot ${statusMeta.dot}"></span>${statusMeta.label}</span></td>
                <td>${titleCell}</td>
                <td class="bcast-idx__cell--muted">${scheduled}</td>
                <td class="bcast-idx__cell--num">${audience.toLocaleString()}</td>
                <td class="bcast-idx__cell--num">${sent.toLocaleString()}</td>
                <td class="bcast-idx__cell--num">${failures.toLocaleString()}</td>
                <td class="bcast-idx__cell--num bcast-idx__cell--rate ${rateClass}">${rateLabel}</td>
                <td class="bcast-idx__col--actions">${duplicateAction}</td>
            </tr>
        `;
    }

    onSelectAllChange() {
        const checked = this.hasSelectAllTarget && this.selectAllTarget.checked;
        this.rowsTarget.querySelectorAll('input[data-bcast-checkbox]').forEach((cb) => {
            cb.checked = checked;
        });
        this.updateBulkActionsVisibility();
    }

    updateBulkActionsVisibility() {
        const tab = this.tabValue || 'active';
        const checkboxes = this.rowsTarget.querySelectorAll('input[data-bcast-checkbox]:checked');
        const count = checkboxes.length;

        if (this.hasSelectAllTarget) {
            const all = this.rowsTarget.querySelectorAll('input[data-bcast-checkbox]');
            this.selectAllTarget.checked = all.length > 0 && count === all.length;
            this.selectAllTarget.indeterminate = count > 0 && count < all.length;
        }

        const showArchive = tab === 'active' && this.archiveUrlValue && count > 0;
        const showRestore = tab === 'archived' && this.restoreUrlValue && count > 0;

        if (this.hasArchiveBtnTarget) this.show(this.archiveBtnTarget, showArchive);
        if (this.hasRestoreBtnTarget) this.show(this.restoreBtnTarget, showRestore);
    }

    getSelectedIds() {
        return [...this.rowsTarget.querySelectorAll('input[data-bcast-checkbox]:checked')]
            .map((cb) => parseInt(cb.dataset.broadcastId, 10))
            .filter((id) => !Number.isNaN(id));
    }

    async archiveSelected() {
        const ids = this.getSelectedIds();
        if (ids.length === 0 || !this.archiveUrlValue) return;
        await this.submitBulkAction(this.archiveUrlValue, ids, this.archiveCsrfTokenValue);
    }

    async restoreSelected() {
        const ids = this.getSelectedIds();
        if (ids.length === 0 || !this.restoreUrlValue) return;
        await this.submitBulkAction(this.restoreUrlValue, ids, this.restoreCsrfTokenValue);
    }

    async submitBulkAction(url, ids, csrfToken) {
        const body = { ids, _token: csrfToken };
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
        });
        if (res.redirected) {
            window.location.href = res.url;
        } else if (res.ok) {
            window.location.reload();
        }
    }

    formatDeliveryRate(pct, failures) {
        if (!Number.isFinite(pct)) return '0';
        if (failures > 0 || pct % 1 !== 0) {
            return pct.toFixed(1);
        }
        return String(Math.round(pct));
    }

    // ─── Helpers ────────────────────────────────────────────────────────────────

    /** Toggle element visibility via inline style (works inside web component slots). */
    show(el, visible) {
        if (!el) return;
        el.style.display = visible ? '' : 'none';
    }

    val(el) { return (el?.value || '').toString().trim(); }

    esc(v) {
        return v
            .replaceAll('&', '&amp;').replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;').replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }
}
